import type { CapabilityName } from './models/component';

export type ComponentDeviceKind = 'camera' | 'location' | 'microphone' | 'motion' | 'orientation';
export type ComponentDeviceAvailability = Record<ComponentDeviceKind, boolean>;
export const COMPONENT_DEVICE_IFRAME_POLICY =
  "camera 'none'; microphone 'none'; geolocation 'none'; accelerometer 'none'; gyroscope 'none'; magnetometer 'none'";
export interface ComponentDevicePosition {
  coords: {
    latitude: number;
    longitude: number;
    accuracy: number;
    altitude: number | null;
    altitudeAccuracy: number | null;
    heading: number | null;
    speed: number | null;
  };
  timestamp: number;
}
export interface ComponentDeviceMotion {
  acceleration: { x: number | null; y: number | null; z: number | null } | null;
  accelerationIncludingGravity: { x: number | null; y: number | null; z: number | null } | null;
  rotationRate: { alpha: number | null; beta: number | null; gamma: number | null } | null;
  interval: number;
  timestamp: number;
}
export interface ComponentDeviceOrientation {
  alpha: number | null;
  beta: number | null;
  gamma: number | null;
  absolute: boolean;
  timestamp: number;
}

const DEVICE_METHOD_CAPABILITIES: Record<string, CapabilityName> = {
  'device.camera.capture': 'device.camera.capture',
  'device.location.getCurrent': 'device.location.read',
  'device.microphone.record': 'device.microphone.record',
  'device.motion.subscribe': 'device.motion.read',
  'device.orientation.subscribe': 'device.motion.read',
};

/** 授权按固定方法映射，不能信任 iframe 提交的 capability 或身份。 */
export function componentDeviceCapability(method: unknown): CapabilityName | undefined {
  return typeof method === 'string' &&
    Object.prototype.hasOwnProperty.call(DEVICE_METHOD_CAPABILITIES, method)
    ? DEVICE_METHOD_CAPABILITIES[method]
    : undefined;
}

/** 序列化进隔离 iframe；文件通过结构化克隆传递，设备与授权仍由宿主掌管。 */
export function createComponentDeviceApi<TFile = unknown>(
  call: (method: string, payload: Record<string, unknown>) => Promise<unknown>,
  channel: string,
) {
  const runtime = globalThis as unknown as {
    parent: unknown;
    addEventListener(
      name: string,
      handler: (event: {
        source: unknown;
        data: {
          channel?: string;
          subscriptionId: string;
          stopped?: boolean;
          error?: { message: string };
          data: unknown;
        };
      }) => void,
    ): void;
    console: { warn(message: string): void; error(error: unknown): void };
  };
  const handlers = new Map<string, (data: unknown) => void>();
  const pendingStops = new Set<string>();
  let subscribing = 0;
  runtime.addEventListener('message', (event) => {
    if (event.source !== runtime.parent || event.data?.channel !== channel) return;
    const message = event.data;
    const handler = handlers.get(message.subscriptionId);
    if (message.stopped) {
      if (!handler && subscribing) pendingStops.add(message.subscriptionId);
      handlers.delete(message.subscriptionId);
      if (message.error) runtime.console.warn(message.error.message);
      return;
    }
    if (!handler) return;
    Promise.resolve()
      .then(() => handler(message.data))
      .catch((error) => runtime.console.error(error));
  });
  async function subscribe<T>(method: string, handler: (data: T) => void) {
    if (typeof handler !== 'function') throw new Error('设备订阅处理器必须是函数');
    subscribing++;
    try {
      const result = (await call(method, {})) as { subscriptionId: string } | null;
      if (!result) return null;
      if (typeof result.subscriptionId !== 'string') throw new Error('设备订阅创建失败');
      if (pendingStops.delete(result.subscriptionId)) return () => undefined;
      handlers.set(result.subscriptionId, handler as (data: unknown) => void);
      return () => {
        if (!handlers.delete(result.subscriptionId)) return;
        void call('device.unsubscribe', { subscriptionId: result.subscriptionId }).catch(
          () => undefined,
        );
      };
    } finally {
      subscribing--;
      if (!subscribing) pendingStops.clear();
    }
  }
  return {
    getAvailability: () =>
      call('device.getAvailability', {}) as Promise<ComponentDeviceAvailability>,
    camera: { capture: () => call('device.camera.capture', {}) as Promise<TFile | null> },
    location: {
      getCurrent: () =>
        call('device.location.getCurrent', {}) as Promise<ComponentDevicePosition | null>,
    },
    microphone: { record: () => call('device.microphone.record', {}) as Promise<TFile | null> },
    motion: {
      subscribe: (handler: (data: ComponentDeviceMotion) => void) =>
        subscribe('device.motion.subscribe', handler),
    },
    orientation: {
      subscribe: (handler: (data: ComponentDeviceOrientation) => void) =>
        subscribe('device.orientation.subscribe', handler),
    },
  };
}
